<template>
  <q-select
    v-bind="mergedProps"
    ref="select"
    :value="value"
    :option-label="optionLabel"
    :option-value="optionValue"
    :options="filteredOptions"
    :readonly="readonly"
    @filter="onFilter"
    v-on="$listeners"
    @clear="$emit('input', undefined)"
  >
    <template #no-option>
      <q-item
        v-if="$listeners.ajouter"
        v-ripple
        clickable
        @click="$listeners.ajouter"
      >
        <q-item-section>
          <q-item-label>Aucun résultat</q-item-label>
          <q-item-label caption>
            Créer un nouveau ?
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-icon name="fa fa-plus" />
        </q-item-section>
      </q-item>
    </template>
    <template #append>
      <q-btn
        v-if="$listeners.ajouter && !readonly"
        round
        flat
        icon="fa fa-plus"
        :dense="$attrs.dense"
        class="q-mr-sm"
        @click.stop="$listeners.ajouter"
      />
    </template>
    <template #after>
      <lwf-checkbox
        v-if="nullable"
        :value="value === null"
        hint="Aucun"
        @input="onNullable"
      />
      <lwf-checkbox
        v-if="all"
        v-model="toggleAll"
        hint="Tous"
        @input="_toggleAll"
      />
    </template>
    <template
      v-for="(slot, k) in $slots"
      #[k]
    >
      <slot :name="k" />
    </template>
    <template
      v-for="(slot, k) in $scopedSlots"
      #[k]="props"
    >
      <slot
        :name="k"
        v-bind="props"
      />
    </template>
  </q-select>
</template>

<script>
import LwfCheckbox from 'components/core/LwfCheckbox'
import propsReadonly from '@/mixins/propsReadonly'
import validateProxyMixin from '@/mixins/validateProxyMixin'

/**
 * Autocomplete générique pour LWF
 */
export default {
  name: 'LwfAutocomplete',
  components: { LwfCheckbox },
  mixins: [propsReadonly, validateProxyMixin],
  inheritAttrs: false,
  props: {
    /**
     * @model
     */
    value: {
      type: [Array, Object, String],
      default: undefined
    },
    /**
     * Options du select
     * Le type peut être altéré par option-label et option-value
     */
    options: {
      type: Array,
      default: undefined
    },

    /**
     * Option value
     */
    optionValue: {
      type: String,
      default: 'slug'
    },

    /**
     * Option label
     */
    optionLabel: {
      type: [String, Function],
      default: 'name'
    },

    emitValue: Boolean,

    /**
     * Ajoute une option "Aucun"
     */
    nullable: Boolean,
    all: Boolean,

    multiple: Boolean
  },
  data () {
    return {
      term: '',
      toggleAll: false
    }
  },
  computed: {
    mergedProps () {
      return {
        'use-input': true,
        'hide-selected': true,
        'fill-input': true,
        'input-debounce': 0,
        disable: this.nullable && this.value === null,
        multiple: this.multiple,
        emitValue: this.emitValue,
        ...this.$attrs
      }
    },
    filteredOptions () {
      const { term, options } = this
      const t = _.deburr(term).toUpperCase()
      return term
        ? options.filter(o => _.deburr(JSON.stringify(o).toUpperCase()).indexOf(t) !== -1)
        : options
    }
  },
  watch: {
    options (newOptions, oldOptions) {
      const { emitValue, multiple, toggleAll, value, optionValue } = this
      if (!_.isEqual(newOptions, oldOptions) && multiple && value) {
        if (toggleAll) {
          this._toggleAll()
        } else if (Array.isArray(value) && value.length) {
          const _v = value.filter((e) => newOptions.some(emitValue
            ? (o) => o[optionValue] === e
            : (o) => o[optionValue] === e[optionValue]
          ))
          this.$emit('input', _v)
        }
      }
    },
    value (v) {
      const { options, toggleAll } = this
      if (toggleAll && Array.isArray(v) && options.length !== v.length) {
        this.toggleAll = false
      }
    }
  },
  methods: {
    /**
     * Filtrage par mot clé
     * @param term
     * @param done
     */
    onFilter (term, done) {
      this.term = term
      done()
    },
    /**
     * Modification du paramètre null
     */
    onNullable (v) {
      this.$emit('input', v ? null : [])
    },
    _toggleAll () {
      this.$emit('input', (this.toggleAll)
        ? this.options.map((option) => option[this.optionValue])
        : []
      )
    },
    /**
     * Proxy de la methode focus
     * @public
     */
    focus () {
      this.$refs.select.focus()
    }
  }
}
</script>

<style scoped>

</style>
